import { Descriptions, List, Tag, Avatar, Space, Typography } from "antd";
import { useEffect } from "react";
import { UserOutlined } from "@ant-design/icons";
import Loader from "../../../../common/loader/Loader";
import {
  getBusinessOnboardingThunk,
  selectCurrentBusiness,
  selectCurrentBusinessOnboarding,
  selectIsLoadingBusinessOnboarding,
  resetBusinessOnboarding,
} from "../../../businessesSlice";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import { getFullDate } from "../../../utils/getFullDate";
import { TLPendingWorkflowUserStatus } from "../../../../../interfaces/onboarding.interface";

export default function BusinessOnboarding() {
  const business = useAppSelector(selectCurrentBusiness);
  const onboarding = useAppSelector(selectCurrentBusinessOnboarding);
  const isLoadingBusinessOnboarding = useAppSelector(
    selectIsLoadingBusinessOnboarding
  );

  const dispatch = useAppDispatch();

  useEffect(() => {
    // Reset onboarding data when component mounts or business changes
    dispatch(resetBusinessOnboarding());

    if (business?.externalOnboardingId) {
      fetchBusinessOnboarding();
    }

    // Cleanup function to reset onboarding when component unmounts
    return () => {
      dispatch(resetBusinessOnboarding());
    };
  }, [business?.externalOnboardingId, dispatch]); // Track externalOnboardingId changes and include dispatch in dependencies

  const fetchBusinessOnboarding = async () => {
    if (!business?.externalOnboardingId) return;

    await dispatch(
      getBusinessOnboardingThunk({
        externalOnboardingId: business.externalOnboardingId,
      })
    );
  };

  return (
    <div style={{ position: "relative", minHeight: "100px" }}>
      {!business?.externalOnboardingId ? (
        <div style={{ textAlign: "center", padding: "32px 0" }}>
          <Typography.Title level={4} style={{ color: "#8c8c8c" }}>
            Legacy Onboarding
          </Typography.Title>
          <Typography.Text type="secondary">
            This business was onboarded using the legacy flow before the
            external onboarding system was implemented.
          </Typography.Text>
        </div>
      ) : (
        <>
          {!isLoadingBusinessOnboarding && onboarding?._id?.length > 0 && (
            <>
              <Descriptions
                title="External Onboarding"
                labelStyle={{ opacity: 0.6, width: "130px" }}
              >
                {onboarding?.externalLink && (
                  <Descriptions.Item label="Onboarding Link">
                    <a
                      href={onboarding.externalLink}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {`View full onboarding`}
                    </a>
                  </Descriptions.Item>
                )}

                {onboarding.createdAt && (
                  <Descriptions.Item label="Onboarded At">
                    {getFullDate(onboarding.createdAt)}
                  </Descriptions.Item>
                )}
              </Descriptions>
              {onboarding.pendingWorkflows?.length > 0 && (
                <div style={{ marginTop: 24 }}>
                  <Typography.Title level={5}>
                    Pending Workflows
                  </Typography.Title>
                  <List
                    itemLayout="horizontal"
                    dataSource={onboarding.pendingWorkflows}
                    style={{ width: "100%" }}
                    renderItem={(item) => (
                      <List.Item
                        actions={[
                          <Tag
                            color={
                              item.userStatus ===
                              TLPendingWorkflowUserStatus.WaitingOnEmailClick
                                ? "orange"
                                : item.userStatus ===
                                  TLPendingWorkflowUserStatus.Completed
                                ? "green"
                                : "blue"
                            }
                          >
                            {item.userStatus}
                          </Tag>,
                        ]}
                      >
                        <List.Item.Meta
                          avatar={<Avatar icon={<UserOutlined />} />}
                          title={
                            <Space>
                              <a
                                href={item.link}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {item.name}
                              </a>
                            </Space>
                          }
                          description={
                            <Typography.Text type="secondary">
                              {item.email}
                            </Typography.Text>
                          }
                        />
                      </List.Item>
                    )}
                  />
                </div>
              )}
            </>
          )}
          <div>
            {isLoadingBusinessOnboarding && (
              <Loader text="Fetching Business Onboarding" />
            )}
          </div>
        </>
      )}
    </div>
  );
}
